import {useQuery} from '@tanstack/react-query'
import {lazy, Suspense} from 'react'
import {HashRouter, Link, Route, Routes} from 'react-router-dom'
import {useApi} from './api/ApiContext'
import {useAuth} from './auth/AuthContext'
import {LoadingState} from './components/common/LoadingState'
import {Dashboard} from './pages/Dashboard'
import {LoginPage} from './pages/LoginPage'
import {NotFound} from './pages/NotFound'

const SiteView = lazy(() =>
  import('./pages/SiteView').then((module) => ({default: module.SiteView})),
)
const AssetDetail = lazy(() =>
  import('./pages/AssetDetail').then((module) => ({default: module.AssetDetail})),
)
const AlarmsPage = lazy(() =>
  import('./pages/AlarmsPage').then((module) => ({default: module.AlarmsPage})),
)
const SimulationPage = lazy(() =>
  import('./pages/SimulationPage').then((module) => ({default: module.SimulationPage})),
)

export function App() {
  const {token, authenticating, logout} = useAuth()
  const api = useApi()
  const principal = useQuery({
    queryKey: ['principal', token],
    queryFn: () => api.me(),
    enabled: Boolean(token),
    retry: false,
  })
  if (!token) return <LoginPage />
  if (authenticating || principal.isLoading) {
    return <LoadingState label="Authenticating operator" />
  }
  if (principal.isError) {
    return (
      <main className="login-page">
        <section className="login-panel">
          <h1>Session unavailable</h1>
          <p>{principal.error instanceof Error ? principal.error.message : String(principal.error)}</p>
          <button type="button" onClick={logout}>Clear session</button>
        </section>
      </main>
    )
  }
  return (
    <HashRouter>
      <div className="app-shell">
        <header className="topbar">
          <Link className="brand-lockup" to="/">
            <span>KaldFlyte</span>
            <small>Thermal Operations Console</small>
          </Link>
          <nav>
            <Link to="/">Portfolio</Link>
            <span className="operator">
              {principal.data?.role} · {principal.data?.tenant_id}
            </span>
            <button type="button" onClick={logout}>Sign out</button>
          </nav>
        </header>
        <main className="app-content">
          <Suspense fallback={<LoadingState label="Loading site console" />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/sites/:id" element={<SiteView />} />
              <Route path="/sites/:id/assets/:assetId" element={<AssetDetail />} />
              <Route path="/sites/:id/alarms" element={<AlarmsPage />} />
              <Route path="/sites/:id/simulation" element={<SimulationPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </HashRouter>
  )
}
